import { useState } from 'react';

import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { SiteSelect } from '@/components/common';
import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  type OnFarmImplantSession,
  useCreateOnFarmImplantSession,
} from '@/services/on-farm-implant';
import { useToast } from '@/services/toast';

import { TripCombobox } from '../TripCombobox';

type CreateSessionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (session: OnFarmImplantSession) => void;
};

export const CreateSessionDialog = ({
  open,
  onOpenChange,
  onCreated,
}: CreateSessionDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [sessionDate, setSessionDate] = useState<Date | undefined>(new Date());
  const [siteId, setSiteId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | undefined>();
  const [tripId, setTripId] = useState<string | undefined>();

  const { mutateAsync: createSession, isPending } =
    useCreateOnFarmImplantSession();

  const reset = () => {
    setSessionDate(new Date());
    setSiteId(null);
    setAccountId(undefined);
    setTripId(undefined);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleCreate = async () => {
    if (!sessionDate || !siteId) return;

    try {
      const session = await createSession({
        siteId,
        sessionDate: format(sessionDate, 'yyyy-MM-dd'),
        accountId: accountId ?? null,
        tripId: tripId ?? null,
      });

      reset();
      onOpenChange(false);
      onCreated(session);
    } catch (error) {
      console.error('Failed to create on-farm implant session:', error);
      toast.show({
        severity: 'error',
        summary: t('onFarmImplant.sessions.createFailed'),
        detail: t('onFarmImplant.sessions.createFailedDetail'),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.sessions.createNewSession')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="session-date">
              {t('onFarmImplant.sessions.sessionDate')}
            </label>
            <DatePicker
              id="session-date"
              value={sessionDate}
              onChange={setSessionDate}
              placeholder={t('onFarmImplant.sessions.selectDate')}
              className="h-8 w-full"
            />
          </div>

          <SiteSelect
            label={t('onFarmImplant.entry.implantSite')}
            placeholder={t('onFarmImplant.entry.selectSite')}
            filter="aspiration"
            urlSync={false}
            value={siteId}
            onChange={setSiteId}
            allowClear={false}
            triggerClassName="w-full"
          />

          <div className="flex flex-col gap-2">
            <label>
              {t('onFarmImplant.entry.accountLabel')}{' '}
              <span className="text-gray-400">
                {t('onFarmImplant.entry.optional')}
              </span>
            </label>
            <AccountsCombobox
              value={accountId}
              onChange={setAccountId}
              placeholder={t('onFarmImplant.entry.searchAccountPlaceholder')}
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label>
              {t('onFarmImplant.sessions.tripNumber')}{' '}
              <span className="text-gray-400">
                {t('onFarmImplant.entry.optional')}
              </span>
            </label>
            <TripCombobox
              value={tripId}
              onChange={setTripId}
              placeholder={t('onFarmImplant.sessions.searchTripPlaceholder')}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!sessionDate || !siteId || isPending}
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.sessions.startSession')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
